import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMayCreateContract } from "../award/useMayCreateContract";
import { useCanManageSourcingEvent } from "../useCanManageSourcingEvent";
import {
  AwardLetterStatusDto,
  SeQuestionKind,
  SeQuestionStatus,
  SourcingEventStatus,
  useGetBuyerSeQuestionsQuery,
  useGetAwardLettersQuery,
  useGetEvaluationQuery,
  useListBidsForEventQuery,
  useListParticipationRequestsForBuyerQuery,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import {
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../sourcing-events/buyer/wizard/utils/procedure";
import { requireBooleanEnvVar } from "../../../config";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import type { EventTab } from "../EventTabs";
import { latestRoundOf, panelReadsBids, type NowPanelInput } from "./deriveNowPanel";

const EVALUATING_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
]);

const AWARDED_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
]);

export const useNowPanelData = (
  event: SourcingEventDto,
  activeTab: EventTab,
): NowPanelInput & { retryEvaluation: () => void } => {
  const eventId = event.id;
  const { rounds, roundsLoaded, roundsFailed, retryRounds } = useRoundSelection();
  const { winnerIds, standstillProblem } = useAwardSelection();
  const mayCreateContract = useMayCreateContract(event);
  const mayManage = useCanManageSourcingEvent(event);
  const { me } = useLoggedInWithOrgContextState();
  const now = useCurrentMoment().valueOf();

  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  const collectsRequests = isTwoStageProcedure(procedureType) || isSchemeProcedure(procedureType);

  const { data: questionsData } = useGetBuyerSeQuestionsQuery(
    event.status === SourcingEventStatus.Published ? { eventId } : skipToken,
  );
  const { data: requestsData } = useListParticipationRequestsForBuyerQuery(
    collectsRequests ? { eventId } : skipToken,
  );
  const { data: bidsData, isSuccess: bidsLoaded } = useListBidsForEventQuery(
    panelReadsBids(event.status) ? { eventId } : skipToken,
  );
  // GetEvaluation without `round` evaluates the original competition's bids, not the latest round's.
  const readsEvaluation = EVALUATING_STATUSES.has(event.status);
  const {
    currentData: evaluation,
    isError: evaluationQueryFailed,
    refetch: refetchEvaluation,
  } = useGetEvaluationQuery(
    readsEvaluation && roundsLoaded ? { eventId, round: latestRoundOf(rounds)?.id } : skipToken,
  );
  const { data: awardLetters } = useGetAwardLettersQuery(
    AWARDED_STATUSES.has(event.status) ? { eventId } : skipToken,
  );

  const unansweredQuestions = (questionsData?.questions ?? []).filter(
    (question) =>
      question.kind === SeQuestionKind.Question &&
      question.status === SeQuestionStatus.Unanswered,
  ).length;

  return {
    event,
    now,
    unansweredQuestions,
    participationRequests: requestsData ?? null,
    bids: bidsData?.bids ?? [],
    bidsLoaded,
    selectedWinnerCount: winnerIds.length,
    standstillChoiceIncomplete: standstillProblem != null,
    evaluation: evaluation ?? null,
    evaluationLoaded: !readsEvaluation || evaluation != null,
    evaluationFailed: readsEvaluation && (roundsFailed || evaluationQueryFailed),
    retryEvaluation: () => {
      if (roundsFailed) retryRounds();
      if (evaluationQueryFailed) refetchEvaluation();
    },
    rounds,
    awardLettersSent: awardLetters == null
      ? null
      : awardLetters.letters.every((letter) => letter.status === AwardLetterStatusDto.Sent),
    awardLettersApproved: awardLetters == null
      ? null
      : awardLetters.letters.length > 0 &&
        awardLetters.letters.every((letter) => letter.status === AwardLetterStatusDto.Approved),
    activeTab,
    focusModeEnabled: requireBooleanEnvVar("VITE_ENABLE_FOCUS_MODE"),
    mayCreateContract,
    mayManage,
    meId: me.id,
  };
};
